import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { handleActiveLink, loaderStateTrue, loaderStateFalse } from "../../../../Actions/AllAction";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import DatePicker from "@sentisso/react-modern-calendar-datepicker";
import '@sentisso/react-modern-calendar-datepicker/lib/DatePicker.css';
import * as DashboardController from "../Controller/DashboardController";
import TaskListPopup from "./TaskListPopup";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

// Add custom CSS animations
const customStyles = `
@keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.8; }
    100% { transform: scale(1); opacity: 1; }
}

@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

.rbc-calendar {
    font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
}

.rbc-month-view {
    border: none !important;
}

.rbc-header {
    background: linear-gradient(135deg, rgb(88, 109, 206) 0%, rgb(90, 137, 209) 100%) !important;
    color: white !important;
    font-weight: 600 !important;
    padding: 5px 8px !important;
    font-size: 14px !important;
}

.rbc-date-cell {
    padding: 0px !important;
}

.rbc-off-range-bg {
    background: #f8fafc !important;
}

.rbc-today {
    background: rgba(33, 150, 243, 0.1) !important;
}

.custom-date-cell.is-selected {
    background: transparent !important;
    border: 0px solid #667eea !important;
    border-radius: 0px !important;
    z-index: 10 !important;
}

.custom-date-cell.is-selected .custom-date-label {
    color: #667eea !important;
    font-weight: 600 !important;
}

.date-cell-wrapper.is-selected {
    background: transparent !important;
    border: 1px solid #667eea !important;
    border-radius: 0px !important;
    z-index: 10 !important;
}

.date-cell-wrapper.is-selected .rbc-button-link {
    color: #667eea !important;
    font-weight: 600 !important;
}

/* parent wrapper for "+X more" */
.my-show-more-parent {
    background: rgba(102, 126, 234, 0.08);
    border-radius: 4px;
    padding: 2px 0;
}

/* the "+X more" button itself */
.my-show-more-parent .rbc-show-more {
    font-size: 11px !important;
    font-weight: 600 !important;
    color: #667eea !important;
}

/* Hide all show more buttons */
.rbc-show-more {
    display: none !important;
}
 
/* Hide show more parent containers */
.rbc-row-segment .rbc-show-more {
    display: none !important;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
}

const localizer = momentLocalizer(moment);

const TaskCalendar = ({ tasks, loaderStateTrue, loaderStateFalse, history, refreshTaskData }) => {

    const [apiTaskData, setApiTaskData] = useState(null); // Start with null to indicate no API call made yet
    const [hasApiData, setHasApiData] = useState(false); // Track if we've made an API call
    const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger for calendar UI refresh

    // Use apiTaskData if we've made an API call, otherwise fall back to tasks prop
    const taskData = hasApiData ? (apiTaskData || []) : (tasks && tasks.length > 0 ? tasks : []);

    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [showTaskPopup, setShowTaskPopup] = useState(false);
    const [popupTask, setPopupTask] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [view, setView] = useState("month");
    const [currentDate, setCurrentDate] = useState(new Date());

    // Fetch initial data when component loads
    useEffect(() => {
        fetchTaskData(currentDate, view);
    }, []);

    // Clear selected task when view changes
    useEffect(() => {
        setSelectedTask(null);
        setSelectedDate(null);
        setPopupTask(null);
        setShowTaskPopup(false);
    }, [view, currentDate]);

    // Handle refreshTaskData callback to refresh calendar UI
    useEffect(() => {
        if (refreshTaskData) {
            // Create a wrapped refresh function that triggers calendar refresh
            const wrappedRefreshTaskData = () => {
                setRefreshTrigger(prev => prev + 1); // Trigger calendar refresh
                fetchTaskData(currentDate, view); // Refetch calendar data (this also calls Dashboard refresh)
            };

            // Store the wrapped function for TaskListPopup to use
            window.wrappedRefreshTaskData = wrappedRefreshTaskData;
        }

        return () => {
            // Clean up the window reference
            if (window.wrappedRefreshTaskData) {
                delete window.wrappedRefreshTaskData;
            }
        };
    }, [refreshTaskData, currentDate, view]);

    // Auto-select today's tasks when component loads
    useEffect(() => {
        // console.log("taskData===",taskData)
        const today = moment().format("YYYY-MM-DD");
        const todayTasks = taskData.filter(task => {
            const utcMoment = moment.utc(task.task_date);
            return utcMoment.format("YYYY-MM-DD") === today;
        });

        if (todayTasks.length > 0) {
            setSelectedDate(today);
            if (todayTasks.length === 1) {
                setSelectedTask(todayTasks[0]);
            } else {
                setSelectedTask({ ...todayTasks[0], allTasks: todayTasks, isMultiple: true });
            }
        } else {
            // Clear selection if no tasks for today
            setSelectedTask(null);
            setSelectedDate(null);
        }
    }, [taskData]);

    // Add custom classes to month rows based on children
    useEffect(() => {
        // 1. Handle "+X more" parent
        const showMoreButtons = document.querySelectorAll('.rbc-show-more');

        showMoreButtons.forEach(btn => {
            const parent = btn.parentElement;
            if (parent && !parent.classList.contains('my-show-more-parent')) {
                parent.classList.add('my-show-more-parent');
            }
        });

        // 2. Check all rbc-month-row blocks
        const allMonthRows = document.querySelectorAll('.rbc-month-row');

        allMonthRows.forEach(row => {

            // Condition A: row contains "+X more"
            const hasShowMore = row.querySelector('.my-show-more-parent');
            if (hasShowMore) {
                row.classList.add('my-month-row');
            } else {
                row.classList.remove('my-month-row');
            }

            // Condition B: row contains the current day cell
            const hasCurrent = row.querySelector('.rbc-current');
            if (hasCurrent) {
                row.classList.add('my-current-row');
            } else {
                row.classList.remove('my-current-row');
            }
        });

    }, [taskData, view, currentDate]);





    // Function to fetch task data based on current date and view
    const fetchTaskData = (date, currentView = view) => {
        if (loaderStateTrue && loaderStateFalse) {
            loaderStateTrue();

            // Calculate start and end dates based on current view
            let startDate, endDate;

            if (currentView === 'week') {
                startDate = moment(date).startOf('week').format('YYYY-MM-DD');
                endDate = moment(date).endOf('week').format('YYYY-MM-DD');
            } else if (currentView === 'day') {
                startDate = moment(date).format('YYYY-MM-DD');
                endDate = moment(date).format('YYYY-MM-DD');
            } else {
                // Default to month view
                startDate = moment(date).startOf('month').format('YYYY-MM-DD');
                endDate = moment(date).endOf('month').format('YYYY-MM-DD');
            }

            // console.log("Requesting data for view:", currentView, "Date range:", startDate, "to", endDate);

            let filterData = { "start": startDate, "end": endDate };
            let data = {};
            data["filters"] = JSON.stringify(filterData);

            // console.log("API request payload:", data);

            DashboardController.taskDetails(data).then((response) => {
                loaderStateFalse();
                // console.log("fetch data===", response.data);
                // console.log("API response success:", response.success);

                if (response.success) {
                    // Always set the data, even if it's empty array
                    setApiTaskData(response.data || []);
                    setHasApiData(true); // Mark that we've received API data

                    if (!response.data || response.data.length === 0) {
                        // console.log("No data found, clearing selection");
                        setSelectedTask(null);
                        setSelectedDate(null);
                    }
                } else {
                    // console.log("API response not successful, clearing data");
                    setApiTaskData([]);
                    setHasApiData(true); // Mark that we've made an API call (even if unsuccessful)
                    setSelectedTask(null);
                    setSelectedDate(null);
                }
            }).catch((error) => {
                loaderStateFalse();
                // console.log("TaskCalendar API error:", error);
                setApiTaskData([]);
                setHasApiData(true); // Mark that we've made an API call (even if it failed)
                setSelectedTask(null);
                setSelectedDate(null);
            });
        }
    };

    // Calendar events - create events for react-big-calendar
    const events = useMemo(() => {
        return taskData.map(t => {
            // Parse the UTC date and extract just the date part to avoid timezone issues
            const utcMoment = moment.utc(t.task_date);

            // For calendar display, we want to show the task on the intended date
            // regardless of timezone, so we create a local date with the same date values
            const localMoment = moment({
                year: utcMoment.year(),
                month: utcMoment.month(),
                date: utcMoment.date(),
                hour: utcMoment.hour(),
                minute: utcMoment.minute(),
                second: utcMoment.second()
            });

            // console.log("Original UTC:", t.task_date);
            // console.log("Parsed as local:", localMoment.format());
            // console.log("Local Date object:", localMoment.toDate());

            let startTime, endTime;

            if (view === 'week' || view === 'day') {
                // For week/day view, use the actual time from task_date
                startTime = localMoment.toDate();
                // Set a default duration of 1 hour
                endTime = moment(localMoment).add(1, 'hour').toDate();
            } else {
                // For month view, use allDay events
                startTime = localMoment.toDate();
                endTime = localMoment.toDate();
            }

            return {
                id: t.id,
                title: view === 'month' ? '' : t.task, // Empty title for month view, actual title for week/day view
                start: startTime,
                end: endTime,
                allDay: view === 'month',
                task: t.task,
                taskData: t // Store original task data
            };
        });
    }, [taskData, view]);

    // Event style getter - make events invisible for month view, visible for week/day view with yellow background
    const eventPropGetter = () => {
        if (view === 'month') {
            return {
                style: {
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'transparent',
                    fontSize: 0,
                    height: 0,
                    margin: 0,
                    padding: 0
                }
            };
        } else {
            // For week and day view, use yellow background like month view
            return {
                style: {
                    backgroundColor: '#fff7dd', // Yellow background like month view
                    border: '1px solid #fff7dd',
                    color: '#0c0ca7ff',
                    fontSize: '11px',
                    borderRadius: '4px',
                    padding: '2px 4px',
                    fontWeight: '500',
                    cursor: 'pointer'
                }
            };
        }
    };

    // Modern Custom Month component with enhanced styling
    const CustomMonthDateHeader = ({ date, label }) => {
        const dayTasks = taskData.filter(task => {
            const utcMoment = moment.utc(task.task_date);
            return utcMoment.format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD");
        });
        const hasTask = dayTasks.length > 0;
        const isToday = moment(date).isSame(moment(), 'day');
        const isWeekend = moment(date).day() === 0 || moment(date).day() === 6;
        const isSelected = selectedDate === moment(date).format("YYYY-MM-DD");

        return (
            <div
                className={`custom-date-cell ${hasTask ? 'has-task' : ''} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    const clickedDate = moment(date).format("YYYY-MM-DD");
                    setSelectedDate(clickedDate);

                    if (hasTask) {
                        if (dayTasks.length === 1) {
                            setSelectedTask(dayTasks[0]);
                        } else {
                            setSelectedTask({ ...dayTasks[0], allTasks: dayTasks, isMultiple: true });
                        }
                    } else {
                        // Show no activities message for dates without tasks
                        setSelectedTask({
                            task_date: clickedDate,
                            noActivities: true
                        });
                    }
                }}
                style={{ cursor: 'pointer' }}
            >
                <span className={`custom-date-label ${isToday ? 'is-today' : hasTask ? 'has-task' : isWeekend ? 'is-weekend' : 'default'}`}>{label}</span>
                {hasTask && dayTasks.length === 1 && (
                    <div className="task-badge-single" />
                )}
                {dayTasks.length > 1 && (
                    <div className="task-badge-multiple">
                        {dayTasks.length}
                    </div>
                )}
            </div>
        );
    };

    // Enhanced date cell wrapper with modern styling
    const DateCellWrapper = ({ children, value }) => {
        const dayTasks = taskData.filter(task => {
            const utcMoment = moment.utc(task.task_date);
            return utcMoment.format("YYYY-MM-DD") === moment(value).format("YYYY-MM-DD");
        });
        const hasTask = dayTasks.length > 0;
        const isToday = moment(value).isSame(moment(), 'day');
        const isSelected = selectedDate === moment(value).format("YYYY-MM-DD");

        return (
            <div
                className={`date-cell-wrapper ${hasTask ? 'has-task' : ''} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    const clickedDate = moment(value).format("YYYY-MM-DD");
                    setSelectedDate(clickedDate);

                    if (hasTask) {
                        if (dayTasks.length === 1) {
                            setSelectedTask(dayTasks[0]);
                        } else {
                            setSelectedTask({ ...dayTasks[0], allTasks: dayTasks, isMultiple: true });
                        }
                    } else {
                        // Show no activities message for dates without tasks
                        setSelectedTask({
                            task_date: clickedDate,
                            noActivities: true
                        });
                    }
                }}
                style={{ cursor: 'pointer' }}
            >
                {children}
            </div>
        );
    };

    const formatTaskDate = (dateString) => {
        if (!dateString) return "";
        return moment.utc(dateString).format("DD MMM, YYYY HH:mm:ss");
    };

    return (
        <>
            <div className="col-md-9 task-calendar-container">
                <div className="calender_UI_block">
                    {/* Enhanced Header with Navigation, View Switcher, and Date Range */}
                    <div className="header_sec task-calendar-header">
                        {/* Top Row: Navigation and Date Display */}
                        <div className="task-calendar-nav-row">
                            <button
                                className="task-calendar-nav-btn"
                                onClick={() => {
                                    const newDate = moment(currentDate).subtract(1, view).toDate();
                                    // console.log("back newdate===",newDate)
                                    setCurrentDate(newDate);
                                    // Call API for all views when navigating
                                    setSelectedTask(null);
                                    setSelectedDate(null);
                                    fetchTaskData(newDate, view);
                                }}
                            >
                                ←
                            </button>

                            <div className="task-calendar-date-display">
                                {moment(currentDate).format(view === 'month' ? 'MMM YYYY' : view === 'week' ? 'MMM DD, YY' : 'MMM DD, YY')}
                            </div>

                            <button
                                className="task-calendar-nav-btn"
                                onClick={() => {
                                    const newDate = moment(currentDate).add(1, view).toDate();
                                    // console.log("go newdate===",newDate)

                                    setCurrentDate(newDate);
                                    // Call API for all views when navigating
                                    setSelectedTask(null);
                                    setSelectedDate(null);
                                    fetchTaskData(newDate, view);
                                }}
                            >
                                →
                            </button>
                        </div>

                        {/* Bottom Row: View Switcher and Date Range Picker */}
                        <div className="task-calendar-controls-row">
                            {/* View Switcher */}
                            <div className="task-calendar-view-switcher">
                                <button
                                    className={`task-calendar-view-btn ${view === "month" ? "active" : ""}`}
                                    onClick={() => {
                                        setView("month");
                                        fetchTaskData(currentDate, "month");
                                        setSelectedTask(null);
                                        setSelectedDate(null);
                                    }}
                                >
                                    Month
                                </button>
                                <button
                                    className={`task-calendar-view-btn ${view === "week" ? "active" : ""}`}
                                    onClick={() => {
                                        setView("week");
                                        fetchTaskData(currentDate, "week");
                                        setSelectedTask(null);
                                        setSelectedDate(null);
                                    }}
                                >
                                    Week
                                </button>
                                <button
                                    className={`task-calendar-view-btn ${view === "day" ? "active" : ""}`}
                                    onClick={() => {
                                        setView("day");
                                        fetchTaskData(currentDate, "day");
                                        setSelectedTask(null);
                                        setSelectedDate(null);
                                    }}
                                >
                                    Day
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Side by Side Layout */}
                    <div className="task-calendar-layout">
                        {/* Compact Calendar - Left Side */}
                        <div className="calendar_view task-calendar-view">
                            <Calendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                views={[view]}
                                view={view}
                                date={currentDate}
                                onNavigate={(d) => {
                                    setCurrentDate(d);
                                    // Clear previous selections immediately
                                    setSelectedTask(null);
                                    setSelectedDate(null);
                                    setPopupTask(null);
                                    setShowTaskPopup(false);
                                    // Call API for all navigation changes
                                    fetchTaskData(d, view);
                                }}
                                eventPropGetter={eventPropGetter}
                                components={{
                                    dateCellWrapper: view === 'month' ? DateCellWrapper : undefined,
                                    month: {
                                        dateHeader: CustomMonthDateHeader
                                    },
                                    week: {
                                        event: ({ event }) => (
                                            <div
                                                style={{
                                                    backgroundColor: '#fff7dd', // Yellow background like month view
                                                    border: '1px solid #fff7dd',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    padding: '2px 4px',
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontWeight: "600",
                                                    color: "#F57C00"
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setMousePosition({ x: e.clientX, y: e.clientY });
                                                    setPopupTask(event.taskData);
                                                    // setShowTaskPopup(true);

                                                    // Also update the selected task in the right panel
                                                    const clickedDate = moment(event.taskData.task_date).format("YYYY-MM-DD");
                                                    setSelectedDate(clickedDate);
                                                    setSelectedTask(event.taskData);
                                                }}
                                            >
                                                {event.taskData.task}
                                            </div>
                                        )
                                    },
                                    day: {
                                        event: ({ event }) => (
                                            <div
                                                style={{
                                                    backgroundColor: '#fff7dd', // Yellow background like month view
                                                    border: '1px solid #fff7dd',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    padding: '3px 6px',
                                                    cursor: 'pointer',
                                                    color: '#F57C00',
                                                    fontWeight: '600',
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setMousePosition({ x: e.clientX, y: e.clientY });
                                                    setPopupTask(event.taskData);
                                                    // setShowTaskPopup(true);

                                                    // Also update the selected task in the right panel
                                                    const clickedDate = moment(event.taskData.task_date).format("YYYY-MM-DD");
                                                    setSelectedDate(clickedDate);
                                                    setSelectedTask(event.taskData);
                                                }}
                                            >
                                                {event.taskData.task}
                                            </div>
                                        )
                                    }
                                }}
                                toolbar={false}
                                className="task-calendar-main"
                            />
                        </div>

                        {/* Task Details Panel - Right Side */}
                        <div className="task-details-panel">
                            <div className="task-details-header">
                                <h6 className="task-details-title">
                                    📋 Activities
                                </h6>
                                {selectedTask && (
                                    <div className="task-multiple-header">
                                        <div className="task-multiple-date">
                                            📅 {moment(selectedTask.task_date).format("MMM DD")}
                                        </div>
                                        <div className="task-multiple-count">
                                            {selectedTask.noActivities ?
                                                "0 activities" :
                                                selectedTask.isMultiple && selectedTask.allTasks ?
                                                    `${selectedTask.allTasks.length} activities` :
                                                    "1 activity"
                                            }
                                        </div>
                                    </div>
                                )}

                            </div>

                            <div className="task-details-content">
                                {selectedTask ? (
                                    selectedTask.noActivities ? (
                                        // No Activities on Selected Date
                                        <div className="task-no-selection">
                                            <div className="task-no-selection-icon">
                                                📅
                                            </div>
                                            <div className="task-no-selection-title">
                                                No Activities
                                            </div>
                                            <div className="task-no-selection-text">
                                                No activities found on this date
                                            </div>
                                        </div>
                                    ) : selectedTask.isMultiple && selectedTask.allTasks ? (
                                        // Multiple Tasks Display - Ultra Compact
                                        <div className="task-multiple-container">
                                            <div className="task-multiple-list">
                                                {selectedTask.allTasks.map((task) => (
                                                    <div key={task.id}
                                                        className="task-multiple-item"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMousePosition({ x: e.clientX, y: e.clientY });
                                                            setPopupTask(task);
                                                            setShowTaskPopup(true);
                                                        }}
                                                        style={{ cursor: 'pointer', backgroundColor: task?.original_action_date ? '#ffd382' : '#ffffff' }}>
                                                        <div className="task-multiple-account">
                                                            {task.task.length > 40 ? (
                                                                <OverlayTrigger overlay={<Tooltip>{task.task}</Tooltip>}>
                                                                    <span>{task.task.substring(0, 40) + "..."}</span>
                                                                </OverlayTrigger>
                                                            ) : (
                                                                task.task
                                                            )}
                                                        </div>

                                                        <div className="task-multiple-contact">
                                                            <span>with {task.contact_name}</span>
                                                            <span>{formatTaskDate(task.task_date)}</span>
                                                        </div>

                                                        <div style={{ fontSize: "10px", color: "#64748b" }}>Task owner : {task.task_owner}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        // Single Task Display - Same as Multiple Tasks Display
                                        <div className="task-multiple-container">
                                            <div className="task-multiple-list">
                                                <div
                                                    key={selectedTask.id}
                                                    className="task-multiple-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMousePosition({ x: e.clientX, y: e.clientY });
                                                        setPopupTask(selectedTask);
                                                        setShowTaskPopup(true);
                                                    }}
                                                    style={{ cursor: 'pointer', backgroundColor: selectedTask?.original_action_date ? '#ffd382' : '#ffffff' }}
                                                >
                                                    <div className="task-multiple-account">
                                                        {selectedTask.task.length > 40 ? (
                                                            <OverlayTrigger overlay={<Tooltip>{selectedTask.task}</Tooltip>}>
                                                                <span>{selectedTask.task.substring(0, 40) + "..."}</span>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            selectedTask.task
                                                        )}
                                                    </div>

                                                    <div className="task-multiple-contact">
                                                        <span>with {selectedTask.contact_name}</span>
                                                        <span>{formatTaskDate(selectedTask.task_date)}</span>
                                                    </div>

                                                    <div style={{ fontSize: "10px", color: "#64748b" }}>Task owner : {selectedTask.task_owner}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    // No Task Selected State - Ultra Compact
                                    <div className="task-no-selection">
                                        <div className="task-no-selection-icon">
                                            📅
                                        </div>
                                        <div className="task-no-selection-title">
                                            No Activities
                                        </div>
                                        <div className="task-no-selection-text">
                                            On this selected date
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Task Details Popup */}
            <TaskListPopup
                showTaskPopup={showTaskPopup}
                popupTask={popupTask}
                mousePosition={mousePosition}
                onClose={() => setShowTaskPopup(false)}
                history={history}
                refreshTaskData={refreshTaskData}
            />
        </>
    );
};


const mapStateToProps = (globalState) => ({
    userCredentials: globalState.LoginReducer.userCredentials,
    userLoginDetails: globalState.LoginReducer.userLoginDetails,
    token: globalState.LoginReducer.token,
    localTimeZoneName: globalState.mainReducerData.localTimeZone,
    version: globalState.LoginReducer.version,
});

export default connect(
    mapStateToProps,
    { handleActiveLink, loaderStateTrue, loaderStateFalse }
)(withRouter(TaskCalendar));
